<template>
  <div>
    <div v-if="finishedUpdatingDetails">
      <router-view />
    </div>
    <div v-else id="main-content">
      <template v-if="!isDataNotLoaded">
        <section class="content-page-banner">
          <div class="banner-content">
            <h1 class="heading">{{ $route.meta.title }}</h1>
            <div class="underline"></div>
            <div v-html="refData.memberDetailsInformationText.informationText"></div>
            <div class="member-name">
              <img src="/assets/public/images/mp-icon-make-a-claim.svg" alt="member icon" />
              <h6>{{ memberName }}</h6>
            </div>
          </div>
        </section>
      </template>
      <Alert
        v-if="submitPolicyError"
        :message="ErrorStrings.SUBMIT_POLICY_API_ERROR"
        alertType="error"
      />
      <Alert
        v-else-if="gettingMemberDetailsFailed || gettingRefDataFailed || postingMemberDetailsFailed"
        :message="postingMemberDetailsFailed ? ErrorStrings.SUBMIT_API_ERROR : ErrorStrings.GENERIC_ERROR"
        alertType="error"
      />

      <section id="multi-step-form">
        <MultiStepFormNavigation :currentPage="pageNumber" :links="navLinks()" @linkClicked="updateMemberDetailsPageNum" :disabled="postingMemberDetails" />

        <ApiLoading v-if="isDataNotLoaded" :apiError="gettingMemberDetailsFailed || gettingRefDataFailed" isMemberPortal />

        <form id="form-contents" @submit.prevent novalidate v-else>
          <!-- All the steps are rendered but hidden from visibility for dynamic validation of all steps on load and as some steps validation depend on other step -->
          <MemberInformation v-show="pageNumber === steps.MEMBER_INFORMATION.pageNumber" @updateStepValidation="updateInvalid" v-bind="currentStepComponentProps" />
          <ContactInformation v-show="pageNumber === steps.CONTACT_INFORMATION.pageNumber" @updateStepValidation="updateInvalid" v-bind="currentStepComponentProps" />
          <PrivacyAndAccess v-show="pageNumber === steps.PRIVACY_AND_ACCESS.pageNumber" @updateStepValidation="updateInvalid" v-bind="currentStepComponentProps" />
          <EligibilityInformation
            v-if="memberDetails && memberDetails.eligibility"
            v-show="pageNumber === steps.ELIGIBILITY_INFORMATION.pageNumber"
            @updateStepValidation="updateInvalid"
            v-bind="currentStepComponentProps"
          />
          <StudentDeclaration
            v-show="pageNumber === steps.STUDENT_DECLARATION.pageNumber"
            @updateStepValidation="updateInvalid"
            @scaleChangeRequired="scaleChangeHandler"
            v-bind="currentStepComponentProps"
          />
          <VeteranCardInformation
            v-show="pageNumber === steps.VETERAN_CARD_INFORMATION.pageNumber"
            @updateStepValidation="updateInvalid"
            v-bind="currentStepComponentProps"
            :isReadOnly="true"
          />
          <ConcessionDeclaration
            v-show="pageNumber === steps.CONCESSION_DECLARATION.pageNumber"
            @updateStepValidation="updateInvalid"
            v-bind="currentStepComponentProps"
            :isReadOnly="true"
          />

          <!-- Review and submit is only rendered if pageNumber is equal to it's pageNumber -->
          <ReviewAndSubmit
            v-if="pageNumber === steps.REVIEW_AND_SUBMIT.pageNumber"
            @updateStepValidation="updateInvalid"
            v-bind="currentStepComponentProps"
            :scaleChange="scaleChangeRequired"
          />

          <FormField v-if="pageNumber < steps.REVIEW_AND_SUBMIT.pageNumber">
            <div class="form-page-nav">
              <section class="form-page-nav">
                <button v-if="pageNumber > 1" @click="updateMemberDetailsPageNum(pageNumber - 1)" class="secondary">Back</button>
                <button :disabled="isNextButtonDisabled" v-if="pageNumber < getFinalPageNumber()" @click="updateMemberDetailsPageNum(pageNumber + 1)">Next</button>
              </section>
            </div>
          </FormField>
        </form>
      </section>

      <section v-if="pageNumber === steps.REVIEW_AND_SUBMIT.pageNumber" class="review-submit">
        <div class="wrapper">
          <div v-if="this.isDataChanged() && !postingMemberDetails">
            <p>Please review your changes before pressing submit.</p>
            <p>If something doesn’t look right, go back and edit your details.</p>
          </div>
          <div v-else-if="!this.isDataChanged() && !postingMemberDetails">
            <p>There are no changes to submit.</p>
            <p>If you would like to make a change, go back and edit your details.</p>
          </div>
          <div class="form-page-nav" v-if="!postingMemberDetails">
            <section class="form-page-nav">
              <button @click="updateMemberDetailsPageNum(pageNumber - 1)" class="secondary">Back</button>
              <button :disabled="!this.isDataChanged()" @click="postMemberDetails">Submit</button>
            </section>
          </div>

          <ApiLoading
            v-if="postingMemberDetails"
            :apiError="gettingMemberDetailsFailed"
            isMemberPortal
            loadingMessage="We are processing your request. It shouldn't be too long."
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';
import { VUEX_MEMBER_DETAILS } from '@/constants/VuexConstants';
import { getQueryString } from '@/utils/UrlUtils';
import MemberDetailsServices from '@/services/MemberDetails';
import CommonServices from '@/services/Common';
import { Alert, ApiLoading, FormField, MultiStepFormNavigation } from '@/components';
import { ContactNumberType, EligibilityTypes, NonDependantFlexCodes, UpdateMemberDetailsStepNames } from '@/enums';
import { MY_DETAILS_VIEW_UPDATE_DETAILS, NO_IMPACT_UPDATE_DETAILS, PREMIUM_IMPACT_UPDATE_DETAILS } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { MEMBER_MENU } from '@/constants/pageTitles';
import { createUpdatedMemberDetailsObj, showConcessionCardSection, showVeteranCardSection } from '@/utils';
import { firstLetterToUpperCase, removeWhitespaceFromString, reverseFormatFormDate, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError, under21Years11Month } from '@general/js/utils';
import { ErrorStrings } from '@general/js/constants';
import MemberDetailsMixin from './MemberDetailsFormSteps/MemberDetailsMixin';
import {
  ConcessionDeclaration,
  ContactInformation,
  EligibilityInformation,
  MemberInformation,
  PrivacyAndAccess,
  ReviewAndSubmit,
  StudentDeclaration,
  VeteranCardInformation,
} from './MemberDetailsFormSteps';

export default {
  name: 'ViewUpdateDetails',

  components: {
    Alert,
    ApiLoading,
    ConcessionDeclaration,
    ContactInformation,
    EligibilityInformation,
    FormField,
    MemberInformation,
    MultiStepFormNavigation,
    PrivacyAndAccess,
    ReviewAndSubmit,
    StudentDeclaration,
    VeteranCardInformation,
  },

  mixins: [MemberDetailsMixin],

  data() {
    return {
      gettingMemberDetails: true,
      gettingMemberDetailsFailed: false,
      gettingRefData: true,
      gettingRefDataFailed: false,
      steps: {
        MEMBER_INFORMATION: {
          stepName: UpdateMemberDetailsStepNames.step1,
          pageNumber: 1,
          props: {},
          invalid: false,
          display: 'Member information',
        },
        CONTACT_INFORMATION: {
          stepName: UpdateMemberDetailsStepNames.step2,
          pageNumber: 2,
          props: {},
          invalid: false,
          display: 'Contact information',
        },
        PRIVACY_AND_ACCESS: {
          stepName: UpdateMemberDetailsStepNames.step3,
          pageNumber: 3,
          props: {},
          invalid: false,
          display: 'Privacy and access',
        },
        ELIGIBILITY_INFORMATION: {
          stepName: UpdateMemberDetailsStepNames.step4,
          pageNumber: 4,
          props: {},
          invalid: false,
          display: 'Eligibility information',
        },
        STUDENT_DECLARATION: {
          stepName: UpdateMemberDetailsStepNames.step5,
          pageNumber: 5,
          props: {},
          invalid: false,
          display: 'Student declaration',
        },
        VETERAN_CARD_INFORMATION: {
          stepName: UpdateMemberDetailsStepNames.step6,
          pageNumber: 6,
          props: {},
          invalid: false,
          display: 'Veteran Card information',
        },
        CONCESSION_DECLARATION: {
          stepName: UpdateMemberDetailsStepNames.step7,
          pageNumber: 7,
          props: {},
          invalid: false,
          display: 'Concession information',
        },
        REVIEW_AND_SUBMIT: {
          stepName: UpdateMemberDetailsStepNames.step8,
          pageNumber: 8,
          props: {},
          display: 'Review and submit',
        },
      },
      initialData: {},
      postingMemberDetails: false,
      postingMemberDetailsFailed: false,
      submitPolicyError: false,
      scaleChangeRequired: false,
      ErrorStrings,
    };
  },

  watch: {
    '$route.query.step': {
      handler(val) {
        if (val !== undefined) this.updateMemberDetailsPageNum(Number(val));
      },
    },

    'memberDetails.eligibility.eligibilityType': {
      handler(val, oldVal) {
        this.updatePageStepNumbers();

        if (val !== undefined) {
          this.resetVeteranCardDetailsWhenEligibilityChanges(val, oldVal);
        }
      },
    },

    /** NOTE: this watch must come AFTER `$route.query.step` watcher to make sure always triggered after it */
    '$route.name': {
      async handler(toRouteName, oldRouteName) {
        if (toRouteName === MY_DETAILS_VIEW_UPDATE_DETAILS && (oldRouteName === NO_IMPACT_UPDATE_DETAILS || oldRouteName === PREMIUM_IMPACT_UPDATE_DETAILS)) {
          // whenever navigate from no impact page to form page, reset everything
          this.updateMemberDetailsPageNum(1);
          this.resetMemberDetails();
          await this.getMemberDetails();
        }
      },
    },
  },

  beforeRouteEnter(to, from, next) {
    if (to.name === NO_IMPACT_UPDATE_DETAILS) {
      to.meta.title = MEMBER_MENU.MEMBER_DETAILS.NO_IMPACT;
    } else if (to.name === PREMIUM_IMPACT_UPDATE_DETAILS) {
      to.meta.title = MEMBER_MENU.MEMBER_DETAILS.PREMIUM_IMPACT;
    }
    next();
  },

  beforeRouteLeave(to, from, next) {
    if (this.isDataChanged() && !this.confirmLeave() && !this.submitPolicyInProcess) {
      next(false);
    } else {
      next();
    }
  },

  beforeMount() {
    this.resetMemberDetails(false);

    this.setStepNumber();
  },

  async mounted() {
    await this.getMemberDetails();

    // update steps object to reflect the links displayed in the side nav
    this.updatePageStepNumbers();

    this.$watch('memberDetails.memberInformation.residentialAddress.state', function () {
      this.updatePageStepNumbers();
    });

    window.addEventListener('beforeunload', this.preventLeavePage);
  },

  beforeUnmount() {
    window.removeEventListener('beforeunload', this.preventLeavePage);
    this.resetMemberDetails();
  },

  computed: {
    ...mapGetters('MemberDetails', {
      hasFormBeenUpdated: VUEX_MEMBER_DETAILS.GETTERS.getHasFormBeenUpdated,
      finishedUpdatingDetails: VUEX_MEMBER_DETAILS.GETTERS.getSubmitUpdateSuccessfulStatus,
    }),

    isDataNotLoaded() {
      return this.gettingMemberDetails || this.gettingMemberDetailsFailed || this.gettingRefData || this.gettingRefDataFailed;
    },

    currentStepComponentProps() {
      const baseProps = { initialData: this.initialData };
      const componentMap = {
        [this.steps.MEMBER_INFORMATION.pageNumber]: this.steps.MEMBER_INFORMATION,
        [this.steps.CONTACT_INFORMATION.pageNumber]: this.steps.CONTACT_INFORMATION,
        [this.steps.PRIVACY_AND_ACCESS.pageNumber]: this.steps.PRIVACY_AND_ACCESS,
        [this.steps.ELIGIBILITY_INFORMATION.pageNumber]: this.steps.ELIGIBILITY_INFORMATION,
        [this.steps.STUDENT_DECLARATION.pageNumber]: this.steps.STUDENT_DECLARATION,
        [this.steps.VETERAN_CARD_INFORMATION.pageNumber]: this.steps.VETERAN_CARD_INFORMATION,
        [this.steps.CONCESSION_DECLARATION.pageNumber]: this.steps.CONCESSION_DECLARATION,
        [this.steps.REVIEW_AND_SUBMIT.pageNumber]: this.steps.REVIEW_AND_SUBMIT,
      };

      let thisComponent;
      try {
        thisComponent = componentMap[this.pageNumber];
      } catch (error) {
        logger.log('ViewUpdateDetails:: currentStepComponentProps() - Unexpected page number passed-in', error);
      }

      return { ...baseProps, ...thisComponent.props, ref: thisComponent.stepName };
    },

    memberName() {
      const info = this.memberDetails?.memberInformation;
      return `${info?.firstName} ${info?.lastName}`;
    },

    isNextButtonDisabled() {
      return this.navLinks().filter((obj) => {
        return obj.pages[0] === this.pageNumber + 1;
      })[0]?.disabled;
    },

    isConcessionCardShown() {
      return (
        showConcessionCardSection(
          this.memberDetails?.memberInformation?.residentialAddress?.state,
          this.memberDetails?.memberInformation?.dateOfBirth,
          this.memberDetails?.membershipSummary?.extrasOnly,
        ) && !this.isDependant
      );
    },
  },

  methods: {
    navLinks() {
      // multi step navigation are disabled if fetch data failed or any step invalid
      const step = this.steps;
      const underAge = under21Years11Month(this.memberDetails?.memberInformation?.dateOfBirth);
      const isDependantWithinStudentAge =
        this.isDependant &&
        this?.memberDetails?.memberInformation?.dateOfBirth &&
        this.withinStudentAge(this.reverseFormatFormDate(this.memberDetails.memberInformation.dateOfBirth));
      const navsDisabled = this.isDataNotLoaded;
      const showVeteranCard = showVeteranCardSection(this.isDependant, this.memberDetails?.eligibility?.eligibilityType);
      const showConcessionCard = this.isConcessionCardShown;

      const accessingMemberAccessLevel = this.accessLevel(this.$route?.params?.policyNumber); // access level of current logged in member who is accessing member details
      const thisMemberDetailsIsPolicyHolder = this.memberDetails?.memberInformation?.coveredOnMembershipAsCode === NonDependantFlexCodes.POLICY_HOLDER; // check if member details is for policy holder or not
      const showEligibilityInformation =
        (this.memberDetails?.eligibility !== null ||
          accessingMemberAccessLevel.toLowerCase() === this.AccessLevel.POLICY_HOLDER_ACCESS.toLowerCase() ||
          accessingMemberAccessLevel.toLowerCase() === this.AccessLevel.FULL_ACCESS.toLowerCase()) &&
        thisMemberDetailsIsPolicyHolder;

      const menuLinks = [
        {
          linkTitle: step.MEMBER_INFORMATION.display,
          pages: [step.MEMBER_INFORMATION.pageNumber],
          disabled: navsDisabled,
          hidden: false,
        },
        {
          linkTitle: step.CONTACT_INFORMATION.display,
          pages: [step.CONTACT_INFORMATION.pageNumber],
          disabled: this.getIfStepInvalid(step.CONTACT_INFORMATION.pageNumber) || navsDisabled,
          hidden: false,
        },
        {
          linkTitle: step.PRIVACY_AND_ACCESS.display,
          pages: [step.PRIVACY_AND_ACCESS.pageNumber],
          disabled: this.getIfStepInvalid(step.PRIVACY_AND_ACCESS.pageNumber) || navsDisabled,
          hidden: false,
        },
        {
          linkTitle: step.ELIGIBILITY_INFORMATION.display,
          pages: [step.ELIGIBILITY_INFORMATION.pageNumber],
          disabled: this.getIfStepInvalid(step.ELIGIBILITY_INFORMATION.pageNumber) || navsDisabled,
          hidden: !showEligibilityInformation || this.memberDetails?.eligibility === null,
        },
        {
          linkTitle: step.STUDENT_DECLARATION.display,
          pages: [step.STUDENT_DECLARATION.pageNumber],
          disabled: this.getIfStepInvalid(step.STUDENT_DECLARATION.pageNumber) || navsDisabled,
          hidden: underAge || !isDependantWithinStudentAge,
        },
        {
          linkTitle: step.VETERAN_CARD_INFORMATION.display,
          pages: [step.VETERAN_CARD_INFORMATION.pageNumber],
          disabled: this.getIfStepInvalid(step.VETERAN_CARD_INFORMATION.pageNumber) || navsDisabled,
          hidden: !showVeteranCard,
        },
        {
          linkTitle: step.CONCESSION_DECLARATION.display,
          pages: [step.CONCESSION_DECLARATION.pageNumber],
          disabled: this.getIfStepInvalid(step.CONCESSION_DECLARATION.pageNumber) || navsDisabled,
          hidden: !showConcessionCard,
        },
        {
          linkTitle: step.REVIEW_AND_SUBMIT.display,
          pages: [step.REVIEW_AND_SUBMIT.pageNumber],
          disabled: this.getIfStepInvalid(step.REVIEW_AND_SUBMIT.pageNumber) || navsDisabled,
          hidden: false,
        },
      ];

      const finalMenuLinks = menuLinks.filter((link) => {
        return !link.hidden;
      });

      return finalMenuLinks;
    },

    getFinalPageNumber() {
      const finalLinkNumbers = this.navLinks().map((obj) => {
        return obj.pages[0];
      });
      return finalLinkNumbers.length;
    },

    setStepNumber() {
      const finalPageNumbers = Object.values(this.steps).map((obj) => {
        return obj.pageNumber;
      });

      // we need to check if user is on the conditional step and refresh, then the page set the currentStep to step 1
      // to fill the values for that required for conditional rendering
      // e.g. "Concession information" link relies on data from server like "state" from "residential" address
      const finalLinkNumbers = this.navLinks().map((obj) => {
        return obj.pages[0];
      });
      const difference = finalPageNumbers
        .filter((x) => {
          return !finalLinkNumbers.includes(x);
        })
        .filter((number) => {
          return !Number.isNaN(number);
        });

      // load given step based on `step` query string
      let currentStep = Number(getQueryString('step'));
      if (currentStep && finalPageNumbers.includes(Number(currentStep))) {
        currentStep = Number.isNaN(currentStep) || difference.length !== 0 ? 1 : currentStep;
        this.updateMemberDetailsPageNum(currentStep);
        return currentStep;
      }

      this.updateMemberDetailsPageNum(1);
      return 1;
    },

    /**
     * Update the step page numbers to reflect the dynamic steps.
     *
     * E.g. If Student Declaration is originally page number 5 but it is hidden from the side navigation
     * links, then Student Declaration should be `NaN` while the next page in the sequence should be page 5.
     */
    updatePageStepNumbers() {
      // Convert `this.steps` from an object into an array.
      const allStepsArray = Object.values(this.steps);

      let pageNumberCounter = 1;
      allStepsArray.forEach((step) => {
        // For each step, check if it is currently visible.
        const isVisibleStep = this.navLinks().find((navLink) => {
          return navLink.linkTitle.toLowerCase() === step.display.toLowerCase();
        });

        if (isVisibleStep) {
          // Update the page number using the new counter
          step.pageNumber = pageNumberCounter;
          pageNumberCounter += 1;
        } else {
          step.pageNumber = NaN;
        }
      });

      return allStepsArray;
    },

    updateInvalid(data, stepName) {
      const step = this.steps;
      let currentStep;
      switch (stepName) {
        case step.MEMBER_INFORMATION.stepName:
          currentStep = step.MEMBER_INFORMATION;
          break;

        case step.CONTACT_INFORMATION.stepName:
          currentStep = step.CONTACT_INFORMATION;
          break;

        case step.PRIVACY_AND_ACCESS.stepName:
          currentStep = step.PRIVACY_AND_ACCESS;
          break;

        case step.ELIGIBILITY_INFORMATION.stepName:
          currentStep = step.ELIGIBILITY_INFORMATION;
          break;

        case step.STUDENT_DECLARATION.stepName:
          currentStep = step.STUDENT_DECLARATION;
          break;

        case step.VETERAN_CARD_INFORMATION.stepName:
          currentStep = step.VETERAN_CARD_INFORMATION;
          break;

        case step.CONCESSION_DECLARATION.stepName:
          currentStep = step.CONCESSION_DECLARATION;
          break;

        default:
          break;
      }

      currentStep.invalid = data;
    },

    /**
     * return true if any of previous step from given step is invalid
     */
    getIfStepInvalid(pageNumber) {
      const thisAndPreviousStepsInvalid = [];
      // convert each steps object to array, making sure order is based on the pageNumber
      const eachSteps = Object.values(this.steps).sort((objA, objB) => {
        return objA.pageNumber - objB.pageNumber;
      });

      eachSteps.forEach((obj) => {
        if (obj.pageNumber < pageNumber) {
          thisAndPreviousStepsInvalid.push(obj.invalid);
        }
      });

      return thisAndPreviousStepsInvalid.some(Boolean);
    },

    async getMemberDetails() {
      this.gettingMemberDetails = true;
      this.gettingMemberDetailsFailed = false;
      this.gettingRefData = true;
      this.gettingRefDataFailed = false;

      // fetch ref data
      try {
        const res = await Promise.all([
          MemberDetailsServices.getMemberDetailsPageReferenceData(),
          MemberDetailsServices.getNoImpactPageReferenceData(),
          MemberDetailsServices.getPremiumImpactPageReferenceData(),
        ]);
        // save ref data on vuex
        this.updateMemberDetailsRefData({ ...res[0], ...res[1], ...res[2] });
      } catch (error) {
        logger.error(error);
        this.gettingRefDataFailed = true;
      } finally {
        this.gettingRefData = false;
      }

      // fetch member details
      try {
        const accessingPersonCode = this.$route.params.personCode.split('+')[0];
        const memberNumber = this.$route?.params?.policyNumber;
        let data = await MemberDetailsServices.getMemberDetails(accessingPersonCode, memberNumber);
        data = this.transformData(data);
        this.updateMemberDetails(data);
        this.initialData = cloneDeep(this.memberDetails);
        this.gettingMemberDetails = false;
      } catch (error) {
        logger.error(error);
        this.gettingMemberDetailsFailed = true;
      } finally {
        this.gettingMemberDetails = false;
      }
    },

    async postMemberDetails() {
      this.postingMemberDetails = true;
      this.postingMemberDetailsFailed = false;
      this.submitPolicyError = false;
      this.updateSubmitPolicyInProcess(false);

      // post member details
      try {
        const personCode = this.$route?.params?.personCode;
        const memberNumber = this.$route?.params?.policyNumber;

        const submitDetailsResponse = await MemberDetailsServices.postMemberDetails(this.transformUpdatedData(this.memberDetails, this.memberPolicies), memberNumber, personCode);
        this.postingMemberDetails = false;

        this.updateSubmitUpdateSuccessful(true);

        const currentPremiumAmount = submitDetailsResponse?.currentPremiumAmount;
        const newPremiumAmount = submitDetailsResponse?.newPremiumAmount;

        // Define common route params
        const routeParams = {
          personCode: this.$route.params.personCode,
          name: this.$route?.params?.name,
          policyNumber: this.$route?.params?.policyNumber,
        };

        // Premium Impact
        if (currentPremiumAmount && newPremiumAmount && currentPremiumAmount !== newPremiumAmount) {
          this.$router.push({
            name: PREMIUM_IMPACT_UPDATE_DETAILS,
            params: {
              ...routeParams,
            },
          });
          // No Premium Impact
        } else {
          this.$router.push({
            name: NO_IMPACT_UPDATE_DETAILS,
            params: {
              ...routeParams,
            },
          });
        }
      } catch (error) {
        logger.error(error);
        if (error instanceof SubmitPolicyError) {
          this.submitPolicyError = true;
        } 

        if (error instanceof SubmitPolicyInProcessError || error instanceof SubmitPolicyTimeoutError) {
          this.updateSubmitPolicyInProcess(true);
          this.postingMemberDetails = false;
          this.updateSubmitUpdateSuccessful(true);

          // Raise work item with payload only for timeout error
          if (error instanceof SubmitPolicyTimeoutError) {
            const payload = this.transformUpdatedData(this.memberDetails);
            const request = {
              functionName: "submit-member-details",
              workItemCategory: "OTHERS",
              title: `Submission Timeout for ${this.$route?.params?.policyNumber} in Member Portal`,
              payload,
            };

            await CommonServices.raiseWorkItem(this.$route?.params?.policyNumber, request);
          }
          // Redirect to the success page
          setNavigationState({ flowName: 'update to member details' });
          this.$router.push({
            name: NO_IMPACT_UPDATE_DETAILS,
            params: {
              personCode: this.$route.params.personCode,
              name: this.$route?.params?.name,
              policyNumber: this.$route?.params?.policyNumber,
            },
          });
        } else {
          this.postingMemberDetailsFailed = true;
          this.updateSubmitUpdateSuccessful(false);
          this.navLinks().map((obj) => {
            return {
              ...obj,
              disabled: false,
            };
          });
        }
      } finally {
        this.postingMemberDetails = false;
      }
    },

    // cleaning up the member details object for update API
    transformUpdatedData(memberDetails, updateOtherPolicies) {
      return JSON.parse(JSON.stringify(createUpdatedMemberDetailsObj(memberDetails, this.initialData, updateOtherPolicies)));
    },

    transformData(data) {
      const CONTACT_INFORMATION = data.contactInformation;
      CONTACT_INFORMATION.preferredContactMethods = [];
      if (CONTACT_INFORMATION.mobilePhoneNumber) {
        CONTACT_INFORMATION.preferredContactMethods.push({
          type: firstLetterToUpperCase(ContactNumberType.MOBILE),
          number: CONTACT_INFORMATION.mobilePhoneNumber,
        });
      }
      if (CONTACT_INFORMATION.homePhoneNumber) {
        CONTACT_INFORMATION.preferredContactMethods.push({
          type: firstLetterToUpperCase(ContactNumberType.HOME),
          number: CONTACT_INFORMATION.homePhoneNumber,
        });
      }
      if (CONTACT_INFORMATION.workPhoneNumber) {
        CONTACT_INFORMATION.preferredContactMethods.push({
          type: firstLetterToUpperCase(ContactNumberType.WORK),
          number: CONTACT_INFORMATION.workPhoneNumber,
        });
      }

      if (data?.concessionCard?.cardList && data?.concessionCard?.cardList.length > 0) {
        data.concessionCard.initialCardList = data.concessionCard.cardList.map((card) => {
          const cardObj = {
            ...card,
            op: this.OperationType.NONE,
          };

          if (card.expiryDate) {
            cardObj.expiryDate = reverseFormatFormDate(card.expiryDate);
          }

          if (!this.checkIfItemIsActive(card)) {
            cardObj.hidden = true;
          }

          return cardObj;
        });
        let activeConcessionCards = [];
        activeConcessionCards = data.concessionCard.cardList.filter((card) => {
          return this.checkIfItemIsActive(card);
        });

        if (activeConcessionCards.length > 0) {
          activeConcessionCards.map((card, index) => {
            activeConcessionCards[index] = {
              ...card,
              op: this.OperationType.NONE,
              expiryDate: reverseFormatFormDate(card.expiryDate), // reversing the date from yyyy-mm-dd to dd / mm / yyyy for UI date field
            };
            return true;
          });
        } else {
          // if there is no active cardList
          activeConcessionCards = [
            {
              cardType: '',
              cardNumber: '',
              expiryDate: '',
              startDate: this.getCurrentDate(),
              op: this.OperationType.ADD,
            },
          ];
        }

        data.concessionCard.cardList = activeConcessionCards;
        data.concessionCard.initialActiveCardList = cloneDeep(activeConcessionCards);
      } else {
        // if cardList was null from server but member meets the conditions and can enter details
        data.concessionCard.cardList = [
          {
            cardType: '',
            cardNumber: '',
            expiryDate: '',
            startDate: this.getCurrentDate(),
            op: this.OperationType.ADD,
          },
        ];
        data.concessionCard.initialActiveCardList = [];
        data.concessionCard.initialCardList = [];
      }

      return data;
    },

    confirmLeave() {
      if (this.finishedUpdatingDetails || this.postingMemberDetailsFailed || this.submitPolicyInProcess) return true;
      // eslint-disable-next-line no-alert
      return window.confirm('Do you want to leave? Your unsaved changes will be lost.');
    },

    isDataChanged() {
      if (!this.isDataNotLoaded) {
        const initialData = cloneDeep(this.initialData);
        const memberDetailsData = cloneDeep(this.memberDetails);

        // change `dpId` to `""` if initial data was `null` from api
        [initialData?.memberInformation?.residentialAddress, initialData?.contactInformation?.postalAddress].forEach((addressObj) => {
          if (this.isEmptyString(addressObj?.dpId)) {
            addressObj.dpId = '';
          }
        });

        // remove `op` field from memberDetailsData as initialData would never have them
        delete memberDetailsData?.contactInformation?.postalAddress?.op;

        // for postal address and residential address initial data, if `dpId` is null, set to empty string
        const residentialDpId = initialData?.memberInformation?.residentialAddress?.dpId;
        const postalDpId = initialData?.contactInformation?.postalAddress?.dpId;
        if (residentialDpId === null) {
          initialData.memberInformation.residentialAddress.dpId = '';
        }
        if (postalDpId === null) {
          initialData.contactInformation.postalAddress.dpId = '';
        }

        const initialPreferredContactMethods = initialData.contactInformation.preferredContactMethods;
        const memberDetailsDataPreferredContactMethods = memberDetailsData.contactInformation.preferredContactMethods;
        if (initialPreferredContactMethods.length > 0) {
          initialPreferredContactMethods.forEach((contactMethod) => {
            contactMethod.number = contactMethod.number.replace(/\s/g, '');
          });
        }
        if (memberDetailsDataPreferredContactMethods.length > 0) {
          memberDetailsDataPreferredContactMethods.forEach((contactMethod) => {
            contactMethod.number = contactMethod.number.replace(/\s/g, '');
          });
        }
        const initialPhoneNumber = initialData?.contactInformation?.mobilePhoneNumber;
        if (initialPhoneNumber !== '') {
          initialData.contactInformation.mobilePhoneNumber = removeWhitespaceFromString(initialPhoneNumber);
        }

        const memberPhoneNumber = memberDetailsData?.contactInformation?.mobilePhoneNumber;
        if (memberPhoneNumber !== '') {
          memberDetailsData.contactInformation.mobilePhoneNumber = removeWhitespaceFromString(memberPhoneNumber);
        }

        const initialHomeNumber = initialData?.contactInformation?.homePhoneNumber;
        if (initialHomeNumber !== '') {
          initialData.contactInformation.homePhoneNumber = removeWhitespaceFromString(initialHomeNumber);
        }

        const memberHomeNumber = memberDetailsData?.contactInformation?.homePhoneNumber;
        if (memberHomeNumber !== '') {
          memberDetailsData.contactInformation.homePhoneNumber = removeWhitespaceFromString(memberHomeNumber);
        }

        const initialWorkNumber = initialData?.contactInformation?.workPhoneNumber;
        if (initialWorkNumber !== '') {
          initialData.contactInformation.workPhoneNumber = removeWhitespaceFromString(initialWorkNumber);
        }

        const memberWorkNumber = memberDetailsData?.contactInformation?.workPhoneNumber;
        if (memberWorkNumber !== '') {
          memberDetailsData.contactInformation.workPhoneNumber = removeWhitespaceFromString(memberWorkNumber);
        }

        const initialConcessionCardList = initialData.concessionCard.cardList;
        if (initialConcessionCardList.length === 1 && initialConcessionCardList[0].cardType === '' && initialConcessionCardList[0].cardNumber === '') {
          initialData.concessionCard.cardList = [];
        }

        const memberConcessionCardList = memberDetailsData.concessionCard.cardList;
        if (memberConcessionCardList.length === 1 && memberConcessionCardList[0].cardType === '' && memberConcessionCardList[0].cardNumber === '') {
          memberDetailsData.concessionCard.cardList = [];
        }

        const initialDataVeteranCardList = initialData.veteranCard.veteranCardList;
        if (initialDataVeteranCardList.length === 1 && initialDataVeteranCardList[0].cardType === undefined && initialDataVeteranCardList[0].id === null) {
          initialData.veteranCard.veteranCardList = [];
        }
        // if no veteran card was added set memberDetailsData.veteranCard.veteranCardList to [] as on fetch member details, if list is empty, we are adding initial object hence need to revert it back to compare
        const memberDetailsDataVeteranCardList = memberDetailsData.veteranCard.veteranCardList;
        if (memberDetailsDataVeteranCardList.length === 1 && memberDetailsDataVeteranCardList[0].cardType === undefined && memberDetailsDataVeteranCardList[0].id === null) {
          memberDetailsData.veteranCard.veteranCardList = [];
        }

        return !isEqual(initialData, memberDetailsData);
      }

      return false;
    },

    preventLeavePage(e) {
      // only stop refresh or close browser tab if user made some change without save
      if (this.isDataChanged() && !this.isAutoLogout && this.finishedUpdatingDetails === false && !this.submitPolicyInProcess) {
        // Cancel the event
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = '';
      }
    },

    /**
     * Reset the Veteran Card details based on the newly selected Eligibility Type.
     *
     * These are the rules applied:
     * Scenario 1 - The new Eligibility Type is the same as the old Eligibility Type, then the card details should reflect what was returned by the server.
     * This scenario can be achieved if during the session but prior to submit, the user had changed the Eligibility Type from A to B, then back to A.
     * Scenario 2 - The new Eligibility Type is ADF and the old Eligibility Type is Ex-Serving, or vice versa. The card details should reflect what was returned
     * by the server.
     * Scenario 3 - The new Eligibility Type is Work Connection or Supporting AND the old Eligibility Type is ADF or Ex-Serving, then the `hasVeteranCard` flag
     * should be null and the card details should be end-dated.
     *
     * @param {string} newEligibilityType The newly selected Eligibility Type.
     */
    resetVeteranCardDetailsWhenEligibilityChanges(newEligibilityType) {
      // Get the original Eligibility Type from the server for comparison reasons.
      const initialEligibilityType = this.initialData?.eligibility.eligibilityType;
      const newMemberDetails = cloneDeep(this.memberDetails);

      // Scenario 1:
      if (initialEligibilityType === newEligibilityType) {
        const initialVeteranCardDetails = cloneDeep(this.initialData.veteranCard);
        newMemberDetails.veteranCard = initialVeteranCardDetails; // Reset to the original Veteran Card details from the server.

        this.updateMemberDetails(newMemberDetails);
      }

      /**
       * Scenario 2:
       * No need to have additional logic for scenario 2.
       */

      // Scenario 3:
      else if (
        [...EligibilityTypes.WORK_CONNECTION, ...EligibilityTypes.RELATED].includes(newEligibilityType) &&
        [...EligibilityTypes.SERVING, ...EligibilityTypes.EX_SERVING].includes(initialEligibilityType)
      ) {
        const newVeteranCardList = cloneDeep(this.memberDetails.veteranCard.veteranCardList);
        const newVeteranCardDetails = {
          hasVeteranCard: null,
          veteranCardList: this.endDateActiveVeteranCards(newVeteranCardList),
        };
        newMemberDetails.veteranCard = newVeteranCardDetails;

        this.updateMemberDetails(newMemberDetails);
      }
    },
    scaleChangeHandler(newValue) {
      this.scaleChangeRequired = newValue;
    },
  },
};
</script>

<style lang="scss" scoped>
.top-content {
  background-color: $PRIMARY_GREEN_TINT;
  width: 100%;
  max-width: 1216px;
  margin: 0 auto;

  &.extended-full-width::before {
    content: '';
    height: 100%;
    width: 100vw;
    position: absolute;
    top: 0;
    right: 50%;
    transform: translateX(50%);
    z-index: -1;
    background-color: $PRIMARY_GREEN_TINT;
  }

  .extended-full-width {
    background: $GREY_50;
    margin-top: rem(-50);
  }

  .top-section-content-container {
    display: flex;
    flex-direction: column;
    padding: 3rem 0;
    max-width: 680px;
  }
  .update-info {
    display: inline-block;
    font-size: font-size(-2);
    font-weight: $BOLD;
    padding: 8px 10px 7px 10px;
    &.added {
      background-color: $PRIMARY_GREEN_TINT;
      margin-right: 10px;
    }
    &.removed {
      background-color: $CRIMSON_TINT;
    }
  }

  .heading {
    margin-bottom: 10px;
  }

  .member-name {
    @include flex-row;
    align-items: center;
    gap: 22px;

    img {
      width: 42px;
      height: 42px;
      padding: 11px;
      box-sizing: border-box;
    }

    h6 {
      margin-bottom: 0;
    }
  }
}

.review-submit {
  @include grid-layout;
  @include grid-template(1fr 3fr);

  .wrapper {
    grid-column: 2;
    @include grid-layout;
    @include grid-template(2fr 1fr);
    padding: spacer(3) 0 0;

    @include mobile {
      grid-column: 1;
    }

    & > * {
      grid-column: 1;
    }

    &:before {
      content: '';
      display: block;
      height: calc(100% + #{$CHAT_BUTTON_TOP_MARGIN});
      width: 150vw;
      background: $GREY_50;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);

      @include tablet {
        left: -5%;
      }

      @include mobile {
        width: 100vw;
      }
    }

    .api-loading-wrapper {
      min-height: 80px;
      display: contents;
    }
  }
}

:deep(h4) {
  &.member-details-form-section-header {
    margin-bottom: 0;
  }
}

:deep(caption) {
  &.required-field-indicator {
    display: block;
    margin-bottom: 30px;
    font-size: 14px;
    font-weight: 325;
    text-align: left;
  }
}
</style>
